import * as React from 'react';

import { enumUnit } from '@rematter/frontend-lib-api-client';
import { convertWeightForPricing } from '@rematter/modules/trade/utils/weight';
import { TouchNumberField, weightToPounds } from '@rematter/ui-web';

import { ItemFieldProps } from '../_types';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';

type EstimatedPriceFielsProps = ItemFieldProps & {
  overrideValue?: number;
};

//
// ⚛️ ⚛️ ⚛️ ESTIMATED PRICE FIELD ⚛️ ⚛️ ⚛️
//
export const EstimatedPriceField: React.FC<EstimatedPriceFielsProps> = ({
  lineItem,
  overrideValue,
}) => {
  const t = useTranslations();

  const netWeight = lineItem.weights?.[enumLoadWeightType.Net] ?? null;
  const convertedWeightPounds = weightToPounds(netWeight);
  const convertedNetWeight = convertWeightForPricing(
    convertedWeightPounds,
    lineItem.pricingUnit ?? enumUnit.Pounds,
  );

  const hasPriceResolved =
    lineItem.unitPrice != null ||
    overrideValue != null ||
    lineItem.itemId === null;

  // Memoized values
  const estimatedTotal = React.useMemo(() => {
    if (overrideValue != null) {
      return overrideValue;
    }

    if (lineItem.unitPrice == null) {
      return 0;
    }

    return lineItem.unitPrice.amount * convertedNetWeight;
  }, [convertedNetWeight, lineItem.unitPrice, overrideValue]);

  if (!hasPriceResolved) {
    return (
      <div className="@EstimatedPriceField flex h-full items-center justify-end px-2 text-sm text-neutral/40">
        {t.scaleTicketPro.columns.priceTbd}
      </div>
    );
  }

  return (
    <TouchNumberField
      aria-label={t.scaleTicketPro.columns.estimatedTotal}
      isDisabled
      value={estimatedTotal}
      prefix="$"
      formatOptions={{
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }}
      className="@EstimatedPriceField"
    />
  );
};
